import { ServiceName } from '@deltasierra/integrations-integration-types';
import { BuilderAnimation } from '../builderDocument/animation';
import { ChannelName } from '../builderDocument/builderDocument';
import { BuilderDocumentFormat } from '../builderDocument/builderDocumentFormat';
import { BuilderTemplateFormatType } from '../builderTemplate/BuilderTemplateFormat';
import { Untyped } from '../common/lang/object';
import { tuple } from '../common/lang/tuple';
import { SubChannel } from './SubChannel';

export type ExportableFileFormat = 'GIF' | 'JPG (limited file size)' | 'JPG' | 'MP4' | 'PNG';

export interface FileFormatOptions {
    formats: ExportableFileFormat[];
    defaultFormat: ExportableFileFormat;
}

export class ExternalService {
    public constructor(
        public readonly name: ServiceName,
        public readonly displayName: string,
        public readonly fileFormatOptions?: FileFormatOptions,
        public readonly subChannel?: SubChannel,
    ) { }
}
export const FacebookMarketing = new ExternalService('facebookMarketing', 'Facebook Ads');
export const GoogleMyBusiness = new ExternalService('googleMyBusiness', 'Google Business Profile');
export const GoogleAnalytics = new ExternalService('google', 'Google Analytics');
export const GoogleAdWords = new ExternalService('googleAdWords', 'Google Ads');
export const CampaignMonitor = new ExternalService('campaignMonitor', 'Campaign Monitor');
export const ClubReady = new ExternalService('clubReady', 'ClubReady');
export const Glofox = new ExternalService('glofox', 'Glofox');
export const Kite = new ExternalService('kite', 'Kite');
export const MarianaTek = new ExternalService('marianaTek', 'Mariana Tek');
export const Mindbody = new ExternalService('mindbody', 'Mindbody');
export const Zenoti = new ExternalService('zenoti', 'Zenoti');
export const Facebook = new ExternalService('facebook', 'Facebook', {
    defaultFormat: 'PNG',
    formats: ['PNG'],
});
export const Fitware = new ExternalService('fitware', 'Fitware');
/**
 * 'Instagram' service refers to publishing via the mobila app.
 * 'InstagramDirect' service refers to publishing directly to Instagram via their API.
 * 'InstagramStoryDirect' service refers to publishing directly to Instagram Stories via their API.
 */
export const Instagram = new ExternalService('instagram', 'Instagram', undefined, 'mobileApp');
export const InstagramDirect = new ExternalService(
    'instagram',
    'Instagram',
    {
        defaultFormat: 'JPG',
        formats: ['JPG'],
    },
    'direct',
);
export const InstagramStoryDirect = new ExternalService(
    'instagram',
    'Instagram story',
    {
        defaultFormat: 'JPG',
        formats: ['JPG'],
    },
    'direct',
);
export const Local = new ExternalService('local', 'your local device');
export const LinkedIn = new ExternalService('linkedIn', 'LinkedIn');
export const Mailchimp = new ExternalService('mailchimp', 'Mailchimp');
export const Print = new ExternalService('print', 'your print provider');
export const Twitter = new ExternalService('twitter', 'Twitter', {
    defaultFormat: 'PNG',
    formats: ['PNG', 'JPG'],
});

export const FacebookAndInstagram = [Facebook, Instagram];

export const Multiple = {
    FacebookAndInstagram,
};

export const ExportPlatforms = {
    CampaignMonitor,
    ClubReady,
    Facebook,
    Fitware,
    GoogleMyBusiness,
    Instagram,
    InstagramDirect,
    InstagramStoryDirect,
    LinkedIn,
    Local,
    Mailchimp,
    Multiple,
    Print,
    Twitter,
};

export const PlatformIcons = Object.freeze<Record<ServiceName, string>>({
    campaignMonitor: 'fas fa-envelope',
    clubReady: 'fas fa-envelope',
    facebook: 'fab fa-facebook-square',
    facebookMarketing: 'fab fa-facebook-square',
    fitware: 'fas fa-envelope',
    four51: 'fas fa-print',
    glofox: 'fas fa-glofox',
    google: 'fab fa-google',
    googleAdWords: 'fas fa-ad',
    googleMyBusiness: 'fas fa-store',
    instagram: 'fab fa-instagram',
    kite: 'fas fa-kite',
    linkedIn: 'fab fa-linkedin',
    local: 'fa fa-file',
    mailchimp: 'fab fa-mailchimp',
    mailOps: 'fas fa-envelope',
    marianaTek: 'fab fa-mariana-tek',
    media: 'fas fa-media',
    mindbody: 'fab fa-question',
    mobipost: '',
    print: 'fas fa-print',
    sendgrid: 'fas fa-paper-plane',
    snapchat: 'fab fa-snapchat-square',
    tiktok: 'fab fa-tiktok',
    tv: 'fas fa-tv',
    twitter: 'fab fa-twitter',
    video: 'fas fa-film',
    website: 'fas fa-globe',
    zenoti: 'fas fa-zenoti',
});

export const ChannelDataIcons = Object.freeze<Record<ChannelName, string>>({
    facebook: PlatformIcons.facebook,
    instagram: PlatformIcons.instagram,
    linkedIn: PlatformIcons.linkedIn,
    termsAndConditions: 'fas fa-link',
    twitter: PlatformIcons.twitter,
});

export function getPlatformIcon(service: ExternalService | ServiceName): string {
    if (typeof service === 'string') {
        return PlatformIcons[service];
    } else {
        return PlatformIcons[service.name];
    }
}

export function getChannelDataIcon(channelName: ChannelName): string {
    return ChannelDataIcons[channelName];
}

/**
 * List of platforms that have their authorization/configuration locked down
 * (ie: only the user that added the access token can set/change the configuration).
 * For example: the FB configuration lists FB pages.
 * This list could contain the page names of an agency's client - something that they
 * would rather not expose to other agencies.
 * that who added the access token.
 */
export const ConfigurationLockedDownPlatforms = Object.freeze([
    Facebook,
    FacebookMarketing,
    GoogleAnalytics,
    GoogleAdWords,
    GoogleMyBusiness,
    LinkedIn,
]);

export const FilterableExportPlatforms = [
    ExportPlatforms.Facebook,
    ExportPlatforms.Twitter,
    ExportPlatforms.Instagram,
    ExportPlatforms.InstagramDirect,
    ExportPlatforms.InstagramStoryDirect,
];

export const MobileExportPlatformNames: ServiceName[] = ['facebook', 'instagram', 'twitter', 'local'];

export const MobileSupportedFormatTypes: BuilderTemplateFormatType[] = ['video', 'image'];

export const LocationAccessCheckPlatformNames = tuple(
    'campaignMonitor',
    'clubReady',
    'facebook',
    'fitware',
    'four51',
    'googleMyBusiness',
    'instagram',
    'linkedIn',
    'mailchimp',
    'twitter',
);

export const ClientAccessCheckPlatformNames = tuple('four51');

export type ILocationAccessCheckPlatformName = (typeof LocationAccessCheckPlatformNames)[number];

export type IClientAccessCheckPlatformName = (typeof ClientAccessCheckPlatformNames)[number];

export type IAccessCheckMultiLocationPlatformName =
    | 'clubReady'
    | 'facebook'
    | 'fitware'
    | 'fitwareAutomatedEmail'
    | 'googleMyBusiness'
    | 'instagram'
    | 'linkedIn'
    | 'twitter';

function createExportPlatformsArray(): any[] {
    const output = [];
    for (const key in ExportPlatforms) {
        if (Object.prototype.hasOwnProperty.call(ExportPlatforms, key)) {
            output.push((ExportPlatforms as Untyped)[key]);
        }
    }
    return output;
}
export const ExportPlatformsArray = createExportPlatformsArray();

export class ExportPlatformMap<T> {
    public campaignMonitor: T | null;

    public facebook: T | null;

    public local: T | null;

    public instagram: T | null;

    public print: T | null;

    public twitter: T | null;

    public constructor(defaultValue: T | null = null) {
        this.campaignMonitor = defaultValue;
        this.facebook = defaultValue;
        this.instagram = defaultValue;
        this.local = defaultValue;
        this.print = defaultValue;
        this.twitter = defaultValue;
    }
}

export function getExternalServiceByServiceName(serviceName: ServiceName, subChannel?: SubChannel): ExternalService {
    const allExportablePlatforms = Object.values(ExportPlatforms).filter(
        platform => platform instanceof ExternalService,
    ) as ExternalService[];
    const matchingPlatform = allExportablePlatforms.find(
        platform => platform.name === serviceName && platform.subChannel === subChannel,
    );
    return matchingPlatform ?? ExportPlatforms.Local;
}

export function getFileFormatOptions(
    doc: { format: BuilderDocumentFormat; animation: BuilderAnimation | null },
    platform: ExternalService,
    allowVideo?: boolean,
): FileFormatOptions {
    let fileFormatOptions: FileFormatOptions;
    if (allowVideo !== false && doc.format === BuilderDocumentFormat.video) {
        fileFormatOptions = {
            defaultFormat: 'MP4',
            formats: ['MP4'],
        };
    } else if (doc.animation) {
        fileFormatOptions = {
            defaultFormat: 'GIF',
            formats: ['GIF'],
        };
    } else if (platform.fileFormatOptions) {
        fileFormatOptions = platform.fileFormatOptions;
    } else {
        fileFormatOptions = {
            defaultFormat: 'PNG',
            formats: ['PNG', 'JPG', 'JPG (limited file size)'],
        };
    }
    return fileFormatOptions;
}
